// src/services/workService.js
// Unified Work Service (Tasks • Jobs • Appointments) — production grade
// Mirrors backend routes (see controllers/workController.js & workActionsController.js):
//   GET    /works
//   GET    /works/unscheduled
//   GET    /works/by-number/:workNumber
//   GET    /works/:id
//   POST   /works
//   PATCH  /works/:id
//   PATCH  /works/:id/schedule
//   DELETE /works/:id/schedule
//   POST   /works/:id/transition
//   POST   /works/:id/attach-quote/:quoteId
//   POST   /works/:id/attach-invoice/:invoiceId
//   ── Email actions (new) ─────────────────────────────────────────────────────
//   GET    /works/:id/send/prepare            ← prefill recipients/subject/message
//   POST   /works/:id/send                    ← send confirm/reminder/complete
//   ── Legacy (fallback, if server exposes) ───────────────────────────────────
//   POST   /works/:id/notify
//   POST   /works/:id/notify/preview
//
// Notes:
// - Service stays thin; UI normalization happens in hooks (useWorkQueries).
// - Email “type” must be one of: 'confirm' | 'reminder' | 'complete'
//   (backend also accepts 'completed' and normalizes → 'complete').

import api from '../api/axios';
import { stringify } from 'qs';

/* -------------------------------- constants ------------------------------- */

export const WORK_TYPES = ['task', 'job', 'appointment'];

export const STATUS_BY_TYPE = {
  task: ['todo', 'in_progress', 'blocked', 'done', 'cancelled'],
  job: [
    'new',
    'scheduled',
    'en_route',
    'in_progress',
    'completed',
    'invoiced',
    'cancelled',
    'no_show',
  ],
  appointment: ['planned', 'confirmed', 'in_progress', 'done', 'cancelled', 'no_show'],
};

export const DEFAULT_SORT = 'schedule.start:asc,createdAt:desc';

const BASE = '/works';
const WORK_NUMBER_REGEX = /^(TAS|JOB|APPT)-\d{4}-\d+$/i;

/* -------------------------- idempotency (client) -------------------------- */
// Reuse the same Idempotency-Key for quick repeat clicks on the same work/type.
// TTL mirrors server window (15 min) but is safe to keep short on client.
const IDEM_TTL_MS = 15 * 60 * 1000;
const _idemCache = new Map(); // key: `${workId}|${type}` -> { key, ts }

function _now() {
  return Date.now();
}
function _randomHex(len = 16) {
  try {
    const arr = new Uint8Array(len);
    // eslint-disable-next-line no-undef
    (typeof crypto !== 'undefined' ? crypto : window.crypto).getRandomValues(arr);
    return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    // Fallback
    let s = '';
    for (let i = 0; i < len; i += 1) s += Math.floor(Math.random() * 256).toString(16).padStart(2, '0');
    return s;
  }
}
function _makeIdemKey(workId, type) {
  return `wrk-${String(workId)}-${String(type)}-${_randomHex(12)}`;
}
function getIdempotencyKey(workId, type) {
  const k = `${workId}|${type}`;
  const hit = _idemCache.get(k);
  const now = _now();
  if (hit && now - hit.ts < IDEM_TTL_MS) return hit.key;
  const fresh = _makeIdemKey(workId, type);
  _idemCache.set(k, { key: fresh, ts: now });
  return fresh;
}

/* -------------------------------- helpers --------------------------------- */

const pickPayload = (res) => (res?.data?.data ?? res?.data ?? {});
function safeUpper(s) {
  return typeof s === 'string' ? s.trim().toUpperCase() : s;
}

function assertId(id, label = 'id') {
  const raw =
    typeof id === 'string' || typeof id === 'number'
      ? id
      : id?.id ?? id?._id;
  const val = raw != null ? String(raw) : '';
  if (!val || val === 'undefined') throw new Error(`Invalid ${label}`);
  return val;
}

function cleanParams(obj = {}) {
  const out = {};
  Object.entries(obj).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    if (typeof v === 'string' && v.trim() === '') return;
    out[k] = v;
  });
  return out;
}

function buildQuery(params = {}) {
  const cleaned = cleanParams(params);
  return stringify(cleaned, { arrayFormat: 'repeat', encode: true });
}

function okOrThrow(data) {
  if (data && data.success === false) {
    const err = new Error(data.message || 'Request failed');
    err.code = data.error || 'request_failed';
    throw err;
  }
  return data;
}

function httpErrorToMessage(err) {
  const res = err?.response;
  const data = res?.data;
  const msg =
    data?.message || err?.message || 'Something went wrong. Please try again.';
  const e = new Error(msg);
  e.code = data?.error || err?.code || 'request_failed';
  e.status = res?.status;
  if (data?.conflicts) e.conflicts = data.conflicts;
  if (data) e.data = data;
  return e;
}

// Normalize any array-ish API response shape into an array
function asArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
}

/** Coerce Date-like schedule fields to ISO strings for write operations */
function normalizeScheduleForWrite(schedule = {}) {
  if (!schedule || typeof schedule !== 'object') return undefined;
  const out = { ...schedule };
  const toIso = (v) => {
    if (!v) return v;
    const d = v instanceof Date ? v : new Date(v);
    return Number.isFinite(d.getTime()) ? d.toISOString() : v;
  };
  if ('start' in out) out.start = toIso(out.start);
  if ('end' in out) out.end = toIso(out.end);
  if (Array.isArray(out.exdates)) out.exdates = out.exdates.map(toIso).filter(Boolean);

  // ❌ REMOVED: client-side default timezone
  // if (!out.timezone) out.timezone = 'America/Toronto';
  // ✅ let backend decide when timezone is missing (company default, etc.)
  return out;
}

function stripUndefined(obj = {}) {
  const o = {};
  Object.entries(obj).forEach(([k, v]) => {
    if (v !== undefined) o[k] = v;
  });
  return o;
}

/* ----------------------- minimal filter normalization ---------------------- */

function normalizeListFilters(params = {}) {
  const p = { ...params };

  if (p.workNumber) p.workNumber = safeUpper(p.workNumber);

  if (p.from instanceof Date) p.from = p.from.toISOString();
  if (p.to instanceof Date) p.to = p.to.toISOString();
  if (p.statusChangedSince instanceof Date) {
    p.statusChangedSince = p.statusChangedSince.toISOString();
  }

  if (p.assigneeName != null) p.assigneeName = String(p.assigneeName);
  if (p.clientName != null) p.clientName = String(p.clientName);

  if (Array.isArray(p.status)) p.status = p.status.filter(Boolean);
  if (Array.isArray(p.priority)) p.priority = p.priority.filter(Boolean);

  if (!p.sort) p.sort = DEFAULT_SORT;

  return p;
}

/* ---------------------------- email type utils ----------------------------- */

// Accept both app UI ('complete') and legacy synonyms ('completed', 'completion')
const VALID_EMAIL_TYPES = ['confirm', 'reminder', 'complete'];

function normalizeEmailType(t) {
  const v = String(t || '').toLowerCase();
  if (v === 'completed' || v === 'completion') return 'complete';
  return v;
}

function assertEmailType(t) {
  const norm = normalizeEmailType(t);
  if (!VALID_EMAIL_TYPES.includes(norm)) {
    throw new Error("type must be one of: 'confirm', 'reminder', 'complete'");
  }
  return norm;
}

/* ---------------------------------- API ----------------------------------- */

/** Quick get by human-readable number (server validates & normalizes) */
export async function getWorkByNumber(workNumber) {
  try {
    const num = safeUpper(workNumber);
    const res = await api.get(`${BASE}/by-number/${encodeURIComponent(num)}`);
    const data = okOrThrow(res.data);
    return data.item ?? data.data?.item ?? data;
  } catch (err) {
    throw httpErrorToMessage(err);
  }
}

/**
 * List Work items.
 */
export async function listWork(params = {}, options = {}) {
  try {
    const normalized = normalizeListFilters(params);

    // Exact WorkNumber fast path
    if (normalized.workNumber && WORK_NUMBER_REGEX.test(normalized.workNumber)) {
      try {
        const item = await getWorkByNumber(normalized.workNumber);
        return {
          items: item ? [item] : [],
          meta: {
            total: item ? 1 : 0,
            page: 1,
            limit: item ? 1 : (normalized.limit || 50),
            totalPages: 1,
          },
        };
      } catch (e) {
        if (e?.status === 404) {
          return {
            items: [],
            meta: { total: 0, page: 1, limit: normalized.limit || 50, totalPages: 1 },
          };
        }
        throw e;
      }
    }

    const relationHints = {
      include: ['client', 'assignees', 'quote', 'invoice'],
      populate: ['client', 'assignees', 'quote', 'invoice'],
      expand: ['client', 'assignees', 'quote', 'invoice'],
    };

    const query = buildQuery({ ...relationHints, ...normalized });
    const res = await api.get(`${BASE}?${query}`, { signal: options.signal });

    const items = Array.isArray(res.data) ? res.data : asArray(res.data);

    const total = res.data?.total ?? items.length;
    const limit = res.data?.limit ?? (normalized.limit || 50);
    const meta = {
      total,
      page: res.data?.page ?? 1,
      limit,
      totalPages: res.data?.totalPages ?? Math.max(1, Math.ceil(total / limit)),
    };

    return { items, meta };
  } catch (err) {
    throw httpErrorToMessage(err);
  }
}

/** Get a single Work item by id */
export async function getWork(id, options = {}) {
  try {
    const workId = assertId(id);
    const res = await api.get(`${BASE}/${workId}`, {
      params: {
        include: ['client', 'assignees', 'quote', 'invoice'],
        populate: ['client', 'assignees', 'quote', 'invoice'],
        expand: ['client', 'assignees', 'quote', 'invoice'],
      },
      signal: options.signal,
    });
    const data = okOrThrow(res.data);
    return data.item ?? data.data?.item ?? data;
  } catch (err) {
    throw httpErrorToMessage(err);
  }
}

/** Create a Work item */
export async function createWork(payload) {
  try {
    if (!payload || typeof payload !== 'object') throw new Error('Invalid payload');
    if (!payload.type || !WORK_TYPES.includes(payload.type)) {
      throw new Error('Invalid or missing work type');
    }

    const body = { ...payload };
    delete body.workNumber;
    delete body.code;

    if (body.schedule) body.schedule = normalizeScheduleForWrite(body.schedule);

    const res = await api.post(BASE, body);
    const data = okOrThrow(res.data);
    return data.item ?? data.data?.item ?? data;
  } catch (err) {
    throw httpErrorToMessage(err);
  }
}

/** Update a Work item (non-status fields) */
export async function updateWork(id, patch) {
  try {
    const workId = assertId(id);
    if (!patch || typeof patch !== 'object') throw new Error('Invalid update payload');

    const body = { ...patch };
    delete body.workNumber; // immutable
    delete body.code;       // deprecated
    if (body.schedule) body.schedule = normalizeScheduleForWrite(body.schedule);

    const res = await api.patch(`${BASE}/${workId}`, body);
    const data = okOrThrow(res.data);
    return data.item ?? data.data?.item ?? data;
  } catch (err) {
    throw httpErrorToMessage(err);
  }
}

/** Transition a Work item status via FSM (optional reason) */
export async function transitionWork(id, nextStatus, reason) {
  try {
    const workId = assertId(id);
    if (!nextStatus || typeof nextStatus !== 'string') {
      throw new Error('Next status is required');
    }
    const res = await api.post(`${BASE}/${workId}/transition`, {
      status: nextStatus,
      ...(reason ? { reason } : {}),
    });
    const data = okOrThrow(res.data);
    return data.item ?? data.data?.item ?? data;
  } catch (err) {
    throw httpErrorToMessage(err);
  }
}

/** Soft delete a Work item */
export async function deleteWork(id) {
  try {
    const workId = assertId(id);
    const res = await api.delete(`${BASE}/${workId}`);
    const data = okOrThrow(res.data);
    return data.item ?? data.data?.item ?? data;
  } catch (err) {
    throw httpErrorToMessage(err);
  }
}

/* ------------------------------ scheduling ops ---------------------------- */

export async function scheduleWork(id, payload = {}) {
  try {
    const workId = assertId(id);
    const {
      start, end, timezone, allDay,
      rrule, exdates,
      assigneeIds,
      primaryAssigneeId,
    } = payload || {};

    const toIso = (v) => {
      if (!v) return v;
      const d = v instanceof Date ? v : new Date(v);
      return Number.isFinite(d.getTime()) ? d.toISOString() : v;
    };

    const body = stripUndefined({
      start: toIso(start),
      end: toIso(end),
      // ❌ previously: timezone: timezone || 'America/Toronto',
      // ✅ now: only send timezone if caller provided one; backend uses company default otherwise
      timezone,
      allDay: !!allDay,
      rrule,
      exdates: Array.isArray(exdates) ? exdates.map(toIso).filter(Boolean) : undefined,
      assigneeIds: Array.isArray(assigneeIds) ? assigneeIds : undefined,
      primaryAssigneeId,
    });

    const res = await api.patch(`${BASE}/${workId}/schedule`, body);
    const data = okOrThrow(res.data);
    return data.event ?? data;
  } catch (err) {
    throw httpErrorToMessage(err);
  }
}

export async function unscheduleWork(id) {
  try {
    const workId = assertId(id);
    const res = await api.delete(`${BASE}/${workId}/schedule`, { data: {} });
    const data = okOrThrow(res.data);
    return data.item ?? data.data?.item ?? data;
  } catch (err) {
    throw httpErrorToMessage(err);
  }
}

/* ------------------------------ tray (read) ------------------------------- */

export async function listUnscheduled(params = {}, options = {}) {
  try {
    const normalized = normalizeListFilters(params);
    const query = buildQuery(cleanParams(normalized));
    const res = await api.get(`${BASE}/unscheduled?${query}`, { signal: options.signal });

    const items = Array.isArray(res.data) ? res.data : asArray(res.data);
    const total = res.data?.total ?? items.length;
    const limit = res.data?.limit ?? (params.limit || 50);
    const meta = {
      total,
      page: res.data?.page ?? 1,
      limit,
      totalPages: res.data?.totalPages ?? Math.max(1, Math.ceil(total / limit)),
    };

    return { items, meta };
  } catch (err) {
    throw httpErrorToMessage(err);
  }
}

/* ------------------------------ linkage ops ------------------------------- */

export async function attachQuote(workIdLike, quoteIdLike) {
  try {
    const workId = assertId(workIdLike, 'workId');
    const quoteId = assertId(quoteIdLike, 'quoteId');
    const res = await api.post(`${BASE}/${workId}/attach-quote/${quoteId}`);
    const data = okOrThrow(res.data);
    return data.item ?? data.data?.item ?? data;
  } catch (err) {
    throw httpErrorToMessage(err);
  }
}

export async function attachInvoice(workIdLike, invoiceIdLike) {
  try {
    const workId = assertId(workIdLike, 'workId');
    const invoiceId = assertId(invoiceIdLike, 'invoiceId');
    const res = await api.post(`${BASE}/${workId}/attach-invoice/${invoiceId}`);
    const data = okOrThrow(res.data);
    return data.item ?? data.data?.item ?? data;
  } catch (err) {
    throw httpErrorToMessage(err);
  }
}

/* ------------------------------ comms (Email) ----------------------------- */

/** Prefill recipients/subject/message for Work emails (new API) */
export async function prepareWorkSend(idLike, { type = 'confirm' } = {}) {
  const workId = assertId(idLike, 'workId');
  const t = assertEmailType(type); // normalize & validate
  try {
    const res = await api.get(`${BASE}/${workId}/send/prepare`, {
      params: { type: t },
    });
    const data = pickPayload(res);
    // Expected shape from controller:
    // { to, cc, bcc, subject, message, type, lastSentAt?, lastEmail?, work: {...}, company: {...} }
    return data;
  } catch (err) {
    throw httpErrorToMessage(err);
  }
}

/**
 * Send a work-related email (Confirm/Reminder/Complete).
 * Accepts:
 *   (workId, type, payload)  OR  (workId, { type, subject?, to?, cc?, bcc?, message?, replyTo?, force? })
 */
export async function sendWorkNotice(idLike, arg2, arg3) {
  const workId = assertId(idLike, 'workId');

  // Normalize arguments
  let type, payload;
  if (typeof arg2 === 'string' || typeof arg2 === 'number') {
    type = assertEmailType(String(arg2)); // normalize & validate
    payload = arg3 || {};
  } else {
    payload = arg2 || {};
    type = assertEmailType(String(payload?.type || '')); // normalize & validate
  }

  const body = stripUndefined({
    type, // already normalized
    subject: payload.subject?.trim() ? payload.subject.trim() : undefined,
    to: Array.isArray(payload.to)
      ? payload.to
      : typeof payload.to === 'string'
      ? [payload.to]
      : undefined,
    cc: Array.isArray(payload.cc)
      ? payload.cc
      : typeof payload.cc === 'string'
      ? [payload.cc]
      : undefined,
    bcc: Array.isArray(payload.bcc)
      ? payload.bcc
      : typeof payload.bcc === 'string'
      ? [payload.bcc]
      : undefined,
    message: payload.message?.trim() ? payload.message.trim() : undefined,
    replyTo: payload.replyTo?.trim() ? payload.replyTo.trim() : undefined,
    // strict boolean only (no truthy coercion)
    force: payload.force === true ? true : undefined,
  });

  // ── Idempotency: stable per-click key reused across quick retries / double clicks
  const idemKey = getIdempotencyKey(workId, type);
  const idemHeaders = {
    // Only the standard header (remove legacy to avoid CORS issues)
    'Idempotency-Key': idemKey,
  };

  // Prefer the new endpoint, fall back to legacy /notify if necessary
  const candidates = [
    `${BASE}/${workId}/send`,    // NEW
    `${BASE}/${workId}/notify`,  // legacy fallback
    `/work/${workId}/notify`,    // extra-legacy
  ];

  const tryPost = async (path) => {
    try {
      return await api.post(path, body, { headers: idemHeaders });
    } catch (e) {
      const code = e?.response?.status;
      if (code === 404 || code === 405 || code === 501) return null;
      throw e;
    }
  };

  let resp = null;
  for (const p of candidates) {
    // eslint-disable-next-line no-await-in-loop
    const attempt = await tryPost(p);
    if (attempt) { resp = attempt; break; }
  }
  if (!resp) throw new Error('No work send endpoint found on the server.');

  try {
    const data = pickPayload(resp);
    const status = data.status || (resp?.status === 202 ? 'queued' : 'ok');

    const queued =
      typeof data.queued === 'boolean'
        ? data.queued
        : (resp?.status === 202 || status === 'queued');

    return {
      ok: true,
      queued,
      status,
      messageKey: data.messageKey ?? data.id ?? null,
      providerMessageId: data.providerMessageId ?? data?.email?.messageId ?? null,
      work: data.work || undefined,
      raw: data,
    };
  } catch (err) {
    const status = err?.response?.status;

    if (status === 409) {
      const data = pickPayload(err.response);
      return {
        ok: true,
        queued: false,
        status: 'duplicate',
        alreadySent: true,
        retryAfter: Number(err.response?.headers?.['retry-after'] || 0) || null,
        raw: data,
      };
    }

    if (status === 429) {
      const data = pickPayload(err.response);
      const retryAfter =
        Number(err.response?.headers?.['retry-after'] || 0) ||
        Number(data?.retryAfter || 0) ||
        null;
      return {
        ok: false,
        rateLimited: true,
        status: 'rate_limited',
        retryAfter,
        raw: data,
      };
    }

    throw httpErrorToMessage(err);
  }
}

/**
 * Preview a work-related notice (subject + text) without sending.
 * Prefers NEW prepare endpoint; falls back to legacy notify/preview if present.
 */
export async function previewWorkNotice(idLike, arg2, arg3) {
  const workId = assertId(idLike, 'workId');

  let type, payload;
  if (typeof arg2 === 'string' || typeof arg2 === 'number') {
    type = assertEmailType(String(arg2)); // normalize & validate
    payload = arg3 || {};
  } else {
    payload = arg2 || {};
    type = assertEmailType(String(payload?.type || '')); // normalize & validate
  }

  // 1) Try NEW prepare endpoint (GET)
  try {
    const pre = await prepareWorkSend(workId, { type });
    const subject =
      (payload.subject && String(payload.subject).trim()) ||
      pre.subject ||
      '';
    const message =
      (payload.message && String(payload.message).trim()) ||
      pre.message ||
      pre.shortMessage ||
      '';
    return {
      subject,
      html: '',      // controller doesn't provide HTML; UI should fall back to text
      text: message,
      shortMessage: message,
      raw: pre,
    };
  } catch (_) {
    // ignore and try legacy below
  }

  // 2) Legacy preview endpoint (POST) if the server still exposes it
  const body = stripUndefined({
    type, // already normalized
    to: Array.isArray(payload.to)
      ? payload.to
      : typeof payload.to === 'string'
      ? [payload.to]
      : undefined,
    cc: Array.isArray(payload.cc)
      ? payload.cc
      : typeof payload.cc === 'string'
      ? [payload.cc]
      : undefined,
    message: payload.message?.trim() ? payload.message.trim() : undefined,
  });

  const candidates = [
    `${BASE}/${workId}/notify/preview`,
    `/work/${workId}/notify/preview`,
  ];

  const tryPost = async (path) => {
    try {
      return await api.post(path, body);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 404 || code === 405 || code === 501) return null;
      throw e;
    }
  };

  let resp = null;
  for (const p of candidates) {
    // eslint-disable-next-line no-await-in-loop
    const attempt = await tryPost(p);
    if (attempt) { resp = attempt; break; }
  }
  if (!resp) {
    // Nothing available; return a minimal preview from provided payload
    const subject = payload.subject?.trim() || '';
    const message = payload.message?.trim() || '';
    return { subject, html: '', text: message, shortMessage: message, raw: {} };
  }

  const data = pickPayload(resp);
  return {
    subject: data.subject || '',
    html: data.html || '',
    text: data.text || '',
    shortMessage: data.shortMessage || '',
    raw: data,
  };
}

/* ------------------------------ conveniences ------------------------------ */

export async function createTask(payload = {}) {
  return createWork({ type: 'task', ...payload });
}
export async function createJob(payload = {}) {
  return createWork({ type: 'job', ...payload });
}
export async function createAppointment(payload = {}) {
  return createWork({ type: 'appointment', ...payload });
}

/** Cosmetic helpers (server is the source of truth for FSM) */
export function allowedStatusesFor(type) {
  return STATUS_BY_TYPE[type] || [];
}
export function canTransition() {
  return true;
}
